import type { RefObject } from 'react'

function ArrowIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

interface LandingPageProps {
  onSelect: () => void
  focusRef: RefObject<HTMLDivElement | null>
}

export default function LandingPage({ onSelect, focusRef }: LandingPageProps) {
  return (
    <div
      ref={focusRef}
      tabIndex={-1}
      className="focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-blue-400"
      style={{
        width: '100%',
        // App.tsx positions this in a `position: absolute; inset: 0` wrapper (itself sized by the
        // App root's .viewport-h, i.e. 100dvh), so this box is always exactly that tall. overflowY:
        // 'auto' below is a safety net if the enlarged header/footer ever need more vertical room
        // than a very short viewport gives.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#0c1424',
        color: '#ffffff',
        overflowY: 'auto',
        overflowX: 'hidden',
        fontFamily: '"Public Sans", system-ui, sans-serif',
        WebkitFontSmoothing: 'antialiased',
        outline: 'none',
      }}>
      {/* Top Bar */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 8,
        padding: '32px 40px',
        background: 'rgba(0,0,0,0.3)',
        position: 'relative',
        zIndex: 10,
        flexShrink: 0,
      }}>
        <h1 className="sr-only">Inglis Enabled Home Showcase — Pennsylvania Assistive Technology &amp; Accessible Housing Map</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img
            src="/inglis-logo-white.png"
            alt="Inglis — Ability and Independence, Redefined"
            width={137}
            height={46}
            style={{ height: 46, width: 'auto' }}
          />
          <span style={{
            fontSize: 13,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.5)',
            paddingLeft: 18,
            borderLeft: '1.5px solid rgba(255,255,255,0.15)',
          }}>
            Enabled Home Showcase
          </span>
        </div>
        <div style={{ fontSize: 17, fontWeight: 500, color: 'rgba(255,255,255,0.55)', textAlign: 'center' }}>
          Pennsylvania&apos;s Assistive Technology &amp; Accessible Housing Map
        </div>
      </header>

      {/* Hero — full-bleed image, no accompanying copy (the header tagline above already
          states the page's purpose). minHeight: 0 lets this shrink smoothly on a short viewport
          instead of forcing a scrollbar — unlike the old panels' text, a decorative
          object-fit: contain image has no meaningful minimum content height.
          Opacity/desaturation + the vignette recede the artwork into the page background
          instead of it reading as a sticker dropped on top (2026-08-17 polish pass, Option A). */}
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, position: 'relative' }}>
        {/* WebP (~199KB) is ~84% smaller than the PNG (~430KB, itself already palette-optimized
            from a 1.2MB original) at identical dimensions — same source artwork, re-exported.
            The PNG stays as the <picture> fallback for the rare browser without WebP support.
            display: contents is load-bearing: as a flex item, <picture> gets blockified and
            sizes itself from the img's intrinsic aspect ratio stretched to 100% width, ignoring
            main's actual height — on a wide/short viewport (any big-screen display) that box
            grows taller than its flex slot and rides up over the header. display: contents
            removes <picture> from the box tree so the img is main's real flex item again, same
            as before this element existed, while still letting <source> pick the format. */}
        <picture style={{ display: 'contents' }}>
          <source srcSet="/landing-hero.webp" type="image/webp" />
          <img
            data-testid="hero-image"
            src="/landing-hero.png"
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.84, filter: 'saturate(0.8) brightness(0.97)' }}
          />
        </picture>
        <div
          data-testid="hero-vignette"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: '-6%',
            background: 'radial-gradient(ellipse 62% 62% at 50% 50%, transparent 46%, #0c1424 92%)',
            pointerEvents: 'none',
          }}
        />
      </main>

      {/* Shared entry point */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '20px 32px',
        background: '#0c1424',
        flexShrink: 0,
        position: 'relative',
        zIndex: 10,
      }}>
        <button
          onClick={() => onSelect()}
          className="outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-white"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 32px',
            borderRadius: 999,
            fontSize: 16,
            fontWeight: 700,
            color: '#ffffff',
            border: '1.5px solid rgba(255,255,255,0.35)',
            background: 'rgba(255,255,255,0.14)',
            cursor: 'pointer',
            minHeight: 44,
            fontFamily: '"Public Sans", system-ui, sans-serif',
          }}
        >
          Explore the Map
          <ArrowIcon />
        </button>
      </div>

      {/* Conference footer. Below ~480px width the two pieces stack full-width (flex-col) so the
          conference line has room to wrap and the Cognitio credit isn't squeezed into a clipped
          nowrap sliver; at/above ~480px they return to the side-by-side row that already works
          well up through desktop widths. Tailwind's default breakpoints top out at sm:640px, so
          an arbitrary min-[480px]: variant is used to land on this specific width. */}
      <footer
        className="flex-col min-[480px]:flex-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 28px',
          // Last element on screen — clear the home-indicator bar in standalone PWA mode.
          paddingBottom: 'calc(18px + env(safe-area-inset-bottom, 0px))',
          background: 'rgba(0,0,0,0.35)',
          fontSize: 15,
          color: 'rgba(255,255,255,0.45)',
          gap: 10,
          zIndex: 10,
          flexShrink: 0,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M12 3v4M12 17v4M5 12H1M23 12h-4M6.3 6.3 3.5 3.5M20.5 20.5l-2.8-2.8M17.7 6.3l2.8-2.8M3.5 20.5l2.8-2.8" />
          </svg>
          Opening Doors Together 2026 · September 9–10 · Harrisburg, PA
        </div>
        <a
          href="https://cognitioanalytics.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="cognitio-credit-link"
          style={{ color: 'rgba(255,255,255,0.65)', letterSpacing: '0.01em', textDecoration: 'none' }}>
          Powered by Cognitio Analytics
        </a>
      </footer>
    </div>
  )
}
